import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { ContractCheckService } from '../shared/contract-check.service';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { PromptDialogComponent } from './prompt-dialog/prompt-dialog.component';
import { PromptAccessLevel, PromptSetApiService, SetRankInput, PromptRankItem, DoraFieldConfig, OutputSchema } from '../shared/api.service';
import { DeletePromptDialogComponent } from './delete-prompt-dialog/delete-prompt-dialog.component';
import { PublishPromptSetInput } from '../shared/api.service';
import { CommonModule } from '@angular/common';
import { CdkDragDrop, moveItemInArray, DragDropModule } from '@angular/cdk/drag-drop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormsModule } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { OutputSchemaComponent } from './output-schema/output-schema.component';
import { SummaryPromptDialogComponent } from './summary-prompt-dialog/summary-prompt-dialog.component';
import { DeleteAllPromptsDialogComponent, DeleteAllPromptsDialogData } from './delete-all-prompts-dialog/delete-all-prompts-dialog.component';

@Component({
    selector: 'app-admin-prompts',
    standalone: true,
    imports: [
        MatExpansionModule,
        MatIconModule,
        MatButtonModule,
        MatDialogModule,
        CommonModule,
        DragDropModule,
        TranslateModule,
        MatSelectModule,
        MatFormFieldModule,
        FormsModule,
        MatTooltipModule,
        OutputSchemaComponent
    ],
    templateUrl: './admin-prompts.component.html',
    styleUrl: './admin-prompts.component.scss'
})
export class AdminPromptsComponent implements OnInit, OnDestroy {
    selectedCheck: any;
    private subscription!: Subscription;
    isLoading = true;
    selectedAccessLevel: PromptAccessLevel = PromptAccessLevel.SUBSCRIPTION_LEVEL;
    
    // Expose PromptAccessLevel enum to template
    PromptAccessLevel = PromptAccessLevel;

    constructor(
        private contractCheckService: ContractCheckService, 
        private dialog: MatDialog,
        private promptSetApiService: PromptSetApiService,
        private snackBar: MatSnackBar,
        private translateService: TranslateService
    ) { }

    ngOnInit() {
        this.selectedCheck = undefined;
        this.isLoading = true;
        this.subscription = this.contractCheckService.selectedCheck$.subscribe(check => {
            // Always update if check is null/undefined, or if it's a different check
            if (!check || check !== this.selectedCheck) {
                this.selectedCheck = check;
                // Sort prompts by rank when loading
                this.sortPromptsByRank();
            }
            this.isLoading = false;
        });
    }

    ngOnDestroy() {
        this.selectedCheck = undefined;
        this.subscription.unsubscribe();
    }

    /**
     * Sort prompts by rank, handling -1 ranks appropriately
     */
    private sortPromptsByRank() {
        if (this.selectedCheck?.prompts) {
            this.selectedCheck.prompts.sort((a: any, b: any) => {
                // Handle cases where rank might be undefined or null
                const rankA = a.rank ?? -1;
                const rankB = b.rank ?? -1;
                
                // If both ranks are -1, maintain original order
                if (rankA === -1 && rankB === -1) {
                    return 0;
                }
                // Items with -1 rank go to the end
                if (rankA === -1) return 1;
                if (rankB === -1) return -1;
                
                // Normal ranking comparison
                return rankA - rankB;
            });
        }
    }

    /**
     * Handle drag and drop reordering
     */
    onPromptDrop(event: CdkDragDrop<any[]>) {
        if (event.previousIndex === event.currentIndex) {
            return;
        }

        // Move the item in the array
        moveItemInArray(this.selectedCheck.prompts, event.previousIndex, event.currentIndex);
        
        // Update rankings and call API
        this.updatePromptRankings();
    }

    /**
     * Update prompt rankings after reordering
     */
    private updatePromptRankings(showSuccessMessage: boolean = true) {
        if (!this.selectedCheck?.prompts) {
            return;
        }

        // Create ranking data
        const promptList: PromptRankItem[] = this.selectedCheck.prompts.map((prompt: any, index: number) => ({
            promptIdentifier: prompt.prompt_identifier,
            name: prompt.name,
            rank: index + 1
        }));

        const setRankInput: SetRankInput = {
            prompt_list: promptList
        };

        // Call API to update rankings
        this.promptSetApiService.setPromptRanking(setRankInput).subscribe({
            next: (response) => {
                // Update local rankings
                this.selectedCheck.prompts.forEach((prompt: any, index: number) => {
                    prompt.rank = index + 1;
                });
                
                if (showSuccessMessage) {
                    this.snackBar.open('Prompt order updated successfully', 'Close', {
                        duration: 3000,
                        horizontalPosition: 'end',
                        verticalPosition: 'top',
                        panelClass: ['success-snackbar']
                    });
                }
            },
            error: (error) => {
                console.error('Error updating prompt rankings:', error);
                this.snackBar.open('Error updating prompt order', 'Close', {
                    duration: 5000,
                    horizontalPosition: 'end',
                    verticalPosition: 'top',
                    panelClass: ['error-snackbar']
                });
            }
        });
    }

    onPanelExpanded(prompt: any) {
        if (prompt.prompt_identifier && (!prompt.context || !prompt.details)) {
            this.promptSetApiService.getPrompt({ promptIdentifier: prompt.prompt_identifier }).subscribe((response) => {
                if (response) {
                    const promptMetadata = response.promptMetadataInformation?.[0];
                    const promptConfig = response.promptConfiguration?.[0];
                    
                    // Update the prompt object with fetched data
                    prompt.context = promptConfig?.prompt?.context || '';
                    
                    // Concatenate all prompt content items if there are multiple
                    if (promptConfig?.prompt?.content) {
                        prompt.details = promptConfig.prompt.content
                            .map((item: any) => item.prompt_content || '')
                            .join('\n\n');
                    } else {
                        prompt.details = '';
                    }
                    
                    prompt.name = promptMetadata?.name || prompt.name;
                    
                    // Store additional data that might be needed later
                    prompt.prompt_set_id = promptMetadata?.promptSetId;
                    prompt.createdAt = promptMetadata?.createdAt;
                    prompt.createdBy = promptConfig?.createdBy;
                    prompt.isPrivate = promptConfig?.isPrivate;
                    prompt.examples = promptConfig?.prompt?.examples || [];
                    prompt.response_format = promptConfig?.prompt?.response_format || '';
                }
            });
        }
    }

    openPromptDialog(prompt?: any) {
        const dialogRef = this.dialog.open(PromptDialogComponent, {
            width: '90vw',
            height: '85vh',
            maxWidth: '1400px',
            data: prompt ? { ...prompt } : this.selectedCheck
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                if (prompt) {
                    // Find the index of the prompt to update
                    const index = this.selectedCheck.prompts.findIndex((p:any) => p.prompt_identifier === prompt.prompt_identifier);
                    if (index !== -1) {
                        // Preserve the original rank when updating
                        const updatedPrompt = { 
                            ...result, 
                            rank: prompt.rank // Preserve the original rank
                        };
                        
                        // Create a new array with the updated prompt
                        this.selectedCheck.prompts = [
                            ...this.selectedCheck.prompts.slice(0, index),
                            updatedPrompt,
                            ...this.selectedCheck.prompts.slice(index + 1)
                        ];
                        
                        // Update rankings on the server after editing the prompt (without showing order message)
                        this.updatePromptRankings(false);
                        
                        // Show appropriate success message for prompt editing
                        this.snackBar.open('Prompt saved successfully', 'Close', {
                            duration: 3000,
                            horizontalPosition: 'end',
                            verticalPosition: 'top',
                            panelClass: ['success-snackbar']
                        });
                    }
                } else {
                    this.createPrompt(result);
                }
            }
        });
    }

    editPrompt(prompt: any) {
        this.openPromptDialog(prompt);
    }

    deletePrompt(prompt: any) {
        const confirmDialog = this.dialog.open(DeletePromptDialogComponent, {
            width: '400px',
            data: prompt
        });

        confirmDialog.afterClosed().subscribe((result: any) => {
            if (result) {
                this.removePrompt(prompt.prompt_identifier);
            }
        });
    }

    /**
     * Delete all prompts in the selected prompt set after confirmation
     */
    deleteAllPrompts(): void {
        if (!this.selectedCheck?.prompts?.length) {
            return;
        }

        const dialogData: DeleteAllPromptsDialogData = {
            promptSetName: this.selectedCheck.name,
            prompts: [...this.selectedCheck.prompts]
        };

        const dialogRef = this.dialog.open(DeleteAllPromptsDialogComponent, {
            width: '450px',
            data: dialogData
        });

        dialogRef.afterClosed().subscribe((result: any) => {
            if (result && result.deletedCount > 0) {
                const message = result.errorCount > 0
                    ? `Deleted ${result.deletedCount} prompts (${result.errorCount} failed)`
                    : `All ${result.deletedCount} prompts deleted successfully`;

                this.snackBar.open(message, 'Close', {
                    duration: 4000,
                    horizontalPosition: 'end',
                    verticalPosition: 'top',
                    panelClass: result.errorCount > 0 ? ['warning-snackbar'] : ['success-snackbar']
                });

                // Refresh data by calling list_prompt_sets API instead of reloading the page
                this.refreshAfterDeleteAll();
            }
        });
    }

    /**
     * Refresh the prompt sets list and re-select the current prompt set after deleting all prompts.
     * Triggers the sidebar to call the list_prompt_sets API instead of reloading the entire page.
     */
    private refreshAfterDeleteAll(): void {
        this.contractCheckService.triggerListRefresh(this.selectedCheck?.prompt_set_id);
    }

    private createPrompt(prompt: any) {
        if (!this.selectedCheck.prompts) {
            this.selectedCheck.prompts = [];
        }
        
        // Assign rank for the new prompt (next available position)
        const newRank = this.selectedCheck.prompts.length + 1;
        prompt.rank = newRank;
        
        // Create a new array with the new prompt
        this.selectedCheck.prompts = [...this.selectedCheck.prompts, { ...prompt }];
        
        // Update rankings on the server after adding the new prompt (without showing order message)
        this.updatePromptRankings(false);
        
        // Show appropriate success message for prompt creation
        this.snackBar.open('Prompt created successfully', 'Close', {
            duration: 3000,
            horizontalPosition: 'end',
            verticalPosition: 'top',
            panelClass: ['success-snackbar']
        });
    }

    private removePrompt(id: string) {
        const index = this.selectedCheck.prompts.findIndex((p:any) => p.prompt_identifier === id);
        if (index !== -1) {
            this.selectedCheck.prompts.splice(index, 1);
            
            // Only update rankings if there are still prompts remaining
            // No need to call sort API if this was the only prompt in the set
            if (this.selectedCheck.prompts.length > 0) {
                this.updatePromptRankings(false);
            }
        }
    }

    deleteSet() {
        // Implement delete logic
    }

    publishSet() {
        if (!this.selectedCheck || !this.selectedCheck.prompt_set_id) {
            return;
        }
        
        const publishInput: PublishPromptSetInput = {
            promptSetId: this.selectedCheck.prompt_set_id,
            access_level: this.selectedAccessLevel
        };
        
        this.promptSetApiService.publishPromptSet(publishInput).subscribe({
            next: (response) => {
                // Update the local state to reflect the published status
                this.selectedCheck.published = true;
                this.selectedCheck.published_at = new Date().toISOString();
                this.selectedCheck.status = 'published';
                this.selectedCheck.access_level = this.selectedAccessLevel;
                
                // Show success toast message with access level info
                const accessLevelText = this.selectedAccessLevel === PromptAccessLevel.PUBLIC ? 'publicly' : 'at subscription level';
                this.snackBar.open(`Prompt set published successfully (${accessLevelText})`, 'Close', {
                    duration: 3000,
                    horizontalPosition: 'end',
                    verticalPosition: 'top',
                    panelClass: ['success-snackbar']
                });
            },
            error: (error) => {
                console.error('Error publishing prompt set:', error);
                
                // Show error toast message
                this.snackBar.open('Error publishing prompt set', 'Close', {
                    duration: 5000,
                    horizontalPosition: 'end',
                    verticalPosition: 'top',
                    panelClass: ['error-snackbar']
                });
            }
        });
    }

    unpublishSet() {
        if (!this.selectedCheck || !this.selectedCheck.prompt_set_id) {
            return;
        }
        
        // Show confirmation dialog
        const confirmed = confirm(
            `Are you sure you want to unpublish "${this.selectedCheck.name}"? ` +
            `This will make it unavailable for contract analysis.`
        );
        
        if (!confirmed) {
            return;
        }
        
        this.promptSetApiService.unpublishPromptSet(this.selectedCheck.prompt_set_id).subscribe({
            next: (response) => {
                // Update the local state to reflect the unpublished status
                this.selectedCheck.published = false;
                this.selectedCheck.published_at = null;
                this.selectedCheck.status = 'draft';
                this.selectedCheck.access_level = null;
                
                // Show success toast message
                this.snackBar.open('Prompt set unpublished successfully', 'Close', {
                    duration: 3000,
                    horizontalPosition: 'end',
                    verticalPosition: 'top',
                    panelClass: ['success-snackbar']
                });
            },
            error: (error) => {
                console.error('Error unpublishing prompt set:', error);
                
                // Show error toast message
                this.snackBar.open('Error unpublishing prompt set', 'Close', {
                    duration: 5000,
                    horizontalPosition: 'end',
                    verticalPosition: 'top',
                    panelClass: ['error-snackbar']
                });
            }
        });
    }

    onSchemaSaved(schema: OutputSchema) {
        // Update the selectedCheck with the new schema
        if (this.selectedCheck) {
            this.selectedCheck.outputSchema = schema;
        }
    }

    openSummaryPromptDialog() {
        if (!this.selectedCheck || !this.selectedCheck.prompt_set_id) {
            return;
        }

        // Get output schema fields
        const outputSchemaFields: DoraFieldConfig[] = this.selectedCheck.outputSchema?.fields || [];

        if (outputSchemaFields.length === 0) {
            this.snackBar.open('Please define output schema fields first', 'Close', {
                duration: 5000,
                horizontalPosition: 'end',
                verticalPosition: 'top',
                panelClass: ['error-snackbar']
            });
            return;
        }

        // Generate summary prompt ID
        const summaryPromptId = `summary_${this.selectedCheck.prompt_set_id.replace(/-/g, '')}`;

        // Fetch existing summary prompt if it exists
        this.promptSetApiService.getPrompt({ promptIdentifier: summaryPromptId }).subscribe({
            next: (existingPrompt) => {
                this.openSummaryDialog(outputSchemaFields, existingPrompt);
            },
            error: (error) => {
                // If prompt doesn't exist, open dialog with no existing data
                this.openSummaryDialog(outputSchemaFields, null);
            }
        });
    }

    private openSummaryDialog(outputSchemaFields: DoraFieldConfig[], existingPrompt: any) {
        const dialogRef = this.dialog.open(SummaryPromptDialogComponent, {
            width: '900px',
            height: '85vh',
            maxWidth: '95vw',
            data: {
                promptSetId: this.selectedCheck.prompt_set_id,
                promptSetName: this.selectedCheck.name,
                outputSchemaFields: outputSchemaFields,
                existingSummaryPrompt: existingPrompt
            }
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                // Refresh the prompt set or show success message
                this.snackBar.open('Summary prompt saved successfully', 'Close', {
                    duration: 3000,
                    horizontalPosition: 'end',
                    verticalPosition: 'top',
                    panelClass: ['success-snackbar']
                });
            }
        });
    }
}
